import { useMemo, useState } from 'react';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { CrawlUrlDepthControls } from '../../features/crawlUrlTree/CrawlUrlDepthControls';
import { CrawlUrlTreeRow } from '../../features/crawlUrlTree/CrawlUrlTreeRow';
import { buildCrawlUrlTree } from '../../features/crawlUrlTree/buildCrawlUrlTree';
import {
  collectExpandableNodeIds,
  collectNodeIdsAtTreeDepth,
  MIN_DISPLAY_DEPTH,
} from '../../features/crawlUrlTree/crawlUrlTreeHelpers';
import { toCrawlUrlNodes } from '../../features/crawlUrlTree/toCrawlUrlNodes';
import { useI18n } from '../../i18n/useI18n';
import type { SelectedUrlNodeDTO } from '../../types/lac';
import {
  LacCrawlSelectedUrlsLevelInfoModal,
  type LacCrawlSelectedUrlsLevelInfoKind,
} from './LacCrawlSelectedUrlsLevelInfoModal';
import './LacCrawlSelectedUrlsDialog.css';

type LacCrawlSelectedUrlsDialogProps = {
  startUrl: string;
  nodes: SelectedUrlNodeDTO[];
  onClose: () => void;
};

export function LacCrawlSelectedUrlsDialog({
  startUrl,
  nodes,
  onClose,
}: LacCrawlSelectedUrlsDialogProps) {
  const { translate } = useI18n();
  const tree = useMemo(
    () => buildCrawlUrlTree(toCrawlUrlNodes(nodes)),
    [nodes],
  );
  const defaultExpandedNodeIds = useMemo(
    () => collectExpandableNodeIds(tree),
    [tree],
  );
  const [expandedNodeIds, setExpandedNodeIds] = useState(
    () => new Set(defaultExpandedNodeIds),
  );
  const [expandedForTree, setExpandedForTree] = useState(tree);
  const [collapseDisplayDepth, setCollapseDisplayDepth] =
    useState(MIN_DISPLAY_DEPTH);
  const [expandDisplayDepth, setExpandDisplayDepth] =
    useState(MIN_DISPLAY_DEPTH);
  const [levelInfo, setLevelInfo] =
    useState<LacCrawlSelectedUrlsLevelInfoKind | null>(null);

  if (tree !== expandedForTree) {
    setExpandedForTree(tree);
    setExpandedNodeIds(new Set(defaultExpandedNodeIds));
  }

  const handleToggleExpanded = (nodeId: string) => {
    setExpandedNodeIds((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const applyDepthExpansion = (displayDepth: number, expand: boolean) => {
    const treeDepth = displayDepth - 1;
    if (!Number.isInteger(treeDepth) || treeDepth < 0) {
      return;
    }

    const nodeIdsAtDepth = collectNodeIdsAtTreeDepth(tree, treeDepth);
    if (nodeIdsAtDepth.length === 0) {
      return;
    }

    setExpandedNodeIds((current) => {
      const next = new Set(current);
      for (const nodeId of nodeIdsAtDepth) {
        if (expand) {
          next.add(nodeId);
        } else {
          next.delete(nodeId);
        }
      }
      return next;
    });
  };

  return (
    <>
      <LiksDialog
        isOpen
        size="xl"
        className="lac-crawl-urls-dialog"
        bodyClassName="lac-crawl-urls-dialog__body"
        title={
          <>
            {translate('lac.websiteCrawls.urlsDialogTitle')}
            <span className="lac-crawl-urls-dialog__subtitle" title={startUrl}>
              {startUrl}
            </span>
          </>
        }
        closeLabel={translate('lac.websiteCrawls.urlsDialogClose')}
        onClose={onClose}
        footer={
          tree.length === 0 ? undefined : (
            <CrawlUrlDepthControls
              collapseDisplayDepth={collapseDisplayDepth}
              expandDisplayDepth={expandDisplayDepth}
              onCollapseDisplayDepthChange={setCollapseDisplayDepth}
              onExpandDisplayDepthChange={setExpandDisplayDepth}
              onApplyCollapseDepth={() =>
                applyDepthExpansion(collapseDisplayDepth, false)
              }
              onApplyExpandDepth={() =>
                applyDepthExpansion(expandDisplayDepth, true)
              }
              onShowCollapseInfo={() => setLevelInfo('collapse')}
              onShowExpandInfo={() => setLevelInfo('expand')}
            />
          )
        }
      >
        {tree.length === 0 ? (
          <p className="lac-crawl-urls-dialog__empty">
            {translate('lac.websiteCrawls.urlsDialogEmpty')}
          </p>
        ) : (
          <div className="lac-crawl-urls-dialog__tree-area">
            <ul className="lac-crawl-urls-dialog__tree">
              {tree.map((node) => (
                <CrawlUrlTreeRow
                  key={node.id}
                  node={node}
                  expandedNodeIds={expandedNodeIds}
                  onToggleExpanded={handleToggleExpanded}
                />
              ))}
            </ul>
          </div>
        )}
      </LiksDialog>
      {levelInfo !== null ? (
        <LacCrawlSelectedUrlsLevelInfoModal
          kind={levelInfo}
          onClose={() => setLevelInfo(null)}
        />
      ) : null}
    </>
  );
}
